import { VideoItem, Rule, TypedVideoAnalyzerPayload } from '../types';

const getInfluencerName = (fileName: string) => (fileName ? fileName.replace(/\.mp4$/i, '') : 'Unknown Video');

const getRuleTextList = (res: TypedVideoAnalyzerPayload, rules: Rule[], status: 'PASS' | 'FAIL', linePrefix = '• ') =>
  res.ruleBreakdown
    .filter((r) => r.status === status)
    .map((r) => {
      const ruleText = rules.find((ru) => ru.id === r.ruleId)?.text || 'Unknown Rule';
      const timeStr = r.timestampStart ? ` [${r.timestampStart}${r.timestampEnd ? '-' + r.timestampEnd : ''}]` : '';
      return `${linePrefix}${ruleText}${timeStr}`;
    })
    .join('\n');

const downloadFile = (content: string, ext: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `video_audit_${new Date().toISOString().slice(0, 10)}.${ext}`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportToCSV = (items: VideoItem[], rules: Rule[]) => {
  const doneItems = items.filter((i) => i.status === 'DONE' && i.result);
  if (doneItems.length === 0) return;

  const headers = ['Video/Influencer Name', 'Do (Followed Guidelines)', "Don't (Violated Guidelines)", 'Suggestion'];

  const rows = doneItems.map((item) => {
    const res = item.result as TypedVideoAnalyzerPayload;
    return [
      getInfluencerName(item.fileName),
      getRuleTextList(res, rules, 'PASS'),
      getRuleTextList(res, rules, 'FAIL'),
      res.suggestions || ''
    ]
      .map((field) => `"${field.replace(/"/g, '""')}"`)
      .join(',');
  });

  const csvContent = [headers.join(','), ...rows].join('\n');
  downloadFile(csvContent, 'csv', 'text/csv;charset=utf-8;');
};

export const exportToSpreadsheet = (items: VideoItem[], rules: Rule[]) => {
  const doneItems = items.filter((i) => i.status === 'DONE' && i.result);
  if (doneItems.length === 0) return;

  const tableRows = doneItems
    .map((item) => {
      const res = item.result as TypedVideoAnalyzerPayload;
      return `
        <tr>
          <td>${getInfluencerName(item.fileName)}</td>
          <td style="white-space: pre-wrap;">${getRuleTextList(res, rules, 'PASS')}</td>
          <td style="white-space: pre-wrap;">${getRuleTextList(res, rules, 'FAIL')}</td>
          <td style="white-space: pre-wrap;">${res.suggestions || ''}</td>
        </tr>
      `;
    })
    .join('');

  const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="UTF-8">
        <!--[if gte mso 9]>
        <xml>
          <x:ExcelWorkbook>
            <x:ExcelWorksheets>
              <x:ExcelWorksheet>
                <x:Name>Audit Summary</x:Name>
                <x:WorksheetOptions>
                  <x:DisplayGridlines/>
                </x:WorksheetOptions>
              </x:ExcelWorksheet>
            </x:ExcelWorksheets>
          </x:ExcelWorkbook>
        </xml>
        <![endif]-->
        <style>
          td { vertical-align: top; padding: 10px; border: 1px solid #ddd; }
          th { background-color: #f0f0f0; padding: 10px; border: 1px solid #999; font-weight: bold; }
        </style>
      </head>
      <body>
        <table>
          <thead>
            <tr>
              <th>Video/Influencer Name</th>
              <th>Do (Followed Guidelines)</th>
              <th>Don't (Violated Guidelines)</th>
              <th>Suggestion</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </body>
      </html>
    `;

  downloadFile(htmlContent, 'xls', 'application/vnd.ms-excel');
};
